import { useQuery } from '@tanstack/react-query';
import { pricingAPI } from '../../lib/api';
import { normalizeCountryCode } from '../../lib/geo/localeCountry';
import { normalizeSelection } from './useCountryPreference';

export type PricingPlan = 'starter' | 'growth' | 'professional' | 'business' | 'enterprise';

export interface PublicPlanPricing {
  plan: PricingPlan;
  monthlyAmountInMinor: number;
  annualAmountInMinor: number | null;
  annualMonthlyEquivalentInMinor: number | null;
  contactSales: boolean;
}

export interface PublicPricing {
  country: string | null;
  market: 'colombia' | 'global';
  marketSource: 'ip' | 'fallback' | 'untrusted_locale_hint' | 'user_selection';
  currencyCode: 'COP' | 'USD';
  quoteVersion: string;
  plans: PublicPlanPricing[];
}

/**
 * @param countryHint    a guess parsed from the device locale; the server only
 *                       trusts it when its own detection produced nothing.
 * @param selectedCountry the buyer's explicit choice, which outranks detection.
 */
export function usePublicPricing(
  countryHint?: string | null,
  selectedCountry?: string | null,
  options?: { enabled?: boolean },
) {
  const normalizedHint = normalizeCountryCode(countryHint) ?? null;
  const normalizedSelection = normalizeSelection(selectedCountry);

  return useQuery<PublicPricing>({
    queryKey: ['public-pricing', normalizedHint, normalizedSelection],
    queryFn: async () => {
      const { data } = await pricingAPI.getPublicPricing(normalizedHint, normalizedSelection);
      return data;
    },
    staleTime: 60_000,
    // FLOSEAI-673 — when the public page is not showing prices, it must not
    // fetch them either. Rendering nothing while still shipping every amount in
    // a network response is hiding, not withdrawing: anyone can read it in
    // devtools.
    enabled: options?.enabled ?? true,
  });
}
